import React, { useState, useEffect } from 'react';
import { useParams, NavLink, Outlet } from 'react-router-dom';
import { useMasterEvent } from '../../context/MasterEventContext';
import { BettingProvider } from '../../context/BettingContext';
import { feedService } from '../../services/feed';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth';
import Avatar from '../shared/Avatar';
import TeamTag from '../shared/TeamTag';
import QuizActivity from './Quiz/QuizActivity';
import VoteActivity from './VoteActivity';
import ShuffleboardActivity from './Shuffleboard/ShuffleboardActivity';
import OpenBetsActivity from './OpenBets/OpenBetsActivity';
import ActivityForm from '../shared/ActivityForm';
import { isActivityHidden, isActivityObfuscated } from '../../services/activityVisibility';
import { isCompetitionActivity } from '../../services/activityTraits';
import TeamSelectionActivity from './TeamSelection/TeamSelectionActivity';
import './ActivityDetail.css';

const TRANSPORT_ICONS = {
  train: '🚆',
  flight: '✈️',
  car: '🚗',
  boat: '⛵',
  bus: '🚌',
  taxi: '🚕',
  walk: '🚶',
  bike: '🚲',
};

const ACCOMMODATION_ICONS = {
  hotel: '🏨',
  hostel: '🛏️',
  airbnb: '🏠',
  camping: '⛺',
  resort: '🏖️',
  cabin: '🏡',
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
}

function ActivityImage({ image }) {
  if (!image) return null;
  if (image.startsWith && image.startsWith('data:')) {
    return <img src={image} alt="activity" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover' }} />;
  }
  return <span style={{ fontSize: 48 }}>{image}</span>;
}

function LocationLink({ location, obfuscated, hint }) {
  if (!location) return null;
  if (obfuscated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <span style={{ fontSize: 16 }}>🙈</span>
        <span style={{ color: '#fbbf24', fontSize: 14, fontStyle: 'italic' }}>
          {hint || 'Location is hidden... you\'ll find out soon!'}
        </span>
      </div>
    );
  }
  const link = location.link || (location.lat && location.lng ? `https://www.google.com/maps?q=${location.lat},${location.lng}` : null);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
      <span style={{ fontSize: 16 }}>📍</span>
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontSize: 14, textDecoration: 'none' }}>
          {location.name || 'View on Map'}
        </a>
      ) : (
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>{location.name || 'Location set'}</span>
      )}
    </div>
  );
}

function ObfuscatedText({ text, obfuscated, hint }) {
  if (!obfuscated || !text) return text || null;
  return <span style={{ color: '#fbbf24', fontStyle: 'italic' }}>{hint || 'Hidden... stay tuned!'}</span>;
}

function CommentsSection({ activityId, masterEventId }) {
  const { user } = useAuth();
  const allUsers = authService.getAllUsers();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentError, setCommentError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const items = await feedService.getForActivity(activityId, 50);
      if (!cancelled) setComments(items);
    }
    load();
    return () => { cancelled = true; };
  }, [activityId]);

  const loadComments = async () => setComments(await feedService.getForActivity(activityId, 50));

  const handlePost = async () => {
    if (!newComment.trim() || !user) return;
    setCommentError('');
    try {
      await feedService.post({ masterEventId, activityId, type: 'comment', userId: user.id, content: newComment.trim() });
      setNewComment('');
      await loadComments();
    } catch (err) {
      setCommentError(err.message || 'Could not post comment');
    }
  };

  return (
    <div style={{ marginTop: 24, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 18 }}>
      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 14px' }}>Comments</h4>
      {commentError && (
        <div style={{ color: '#f87171', fontSize: 12, marginBottom: 8 }}>{commentError}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14, maxHeight: 300, overflowY: 'auto' }}>
        {comments.length === 0 ? (
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>No comments yet. Be the first!</div>
        ) : (
          comments.map((item) => {
            const cu = allUsers.find((u) => u.id === item.userId);
            return (
              <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
                <Avatar user={cu} size={18} style={{ flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>
                  <strong style={{ color: '#fff' }}>{cu?.displayName || 'Unknown'}</strong><TeamTag user={cu} /> {item.content}
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 3 }}>
                    {new Date(item.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
          placeholder="Add a comment..."
          style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none' }} />
        <button onClick={handlePost} disabled={!newComment.trim()}
          style={{ background: '#22c55e', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: newComment.trim() ? 1 : 0.5 }}>
          Post
        </button>
      </div>
    </div>
  );
}

function StatusControls({ activity, canToggle, onToggle }) {
  return (
    <>
      <span className={`actd-status actd-status--${activity.status}`}>{activity.status}</span>
      {canToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="actd-status-toggle"
          title={activity.status === 'draft' ? 'Publish (go live)' : 'Move back to draft'}
        >
          {activity.status === 'draft' ? '🚀 Publish' : '📝 Move to draft'}
        </button>
      )}
    </>
  );
}

function ActivityHeader({ activity, obfuscated, defaultIcon }) {
  const icon = obfuscated ? defaultIcon : (activity.image ? <ActivityImage image={activity.image} /> : defaultIcon);
  const displayName = obfuscated ? (activity.hint?.trim() || 'Hidden activity') : activity.name;
  return (
    <div className="actd-inline-header">
      <span className="actd-inline-icon">{icon}</span>
      <div>
        <h3 className="actd-inline-name">{displayName}</h3>
        {activity.description && (
          <p className="actd-inline-desc">
            <ObfuscatedText text={activity.description} obfuscated={obfuscated} hint={activity.hint} />
          </p>
        )}
      </div>
    </div>
  );
}

function ActivityMeta({ activity, obfuscated }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {activity.date && (
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>📅</span> {obfuscated ? <span style={{ color: '#fbbf24', fontStyle: 'italic' }}>{activity.hint || 'Date hidden...'}</span> : formatDate(activity.date)}
        </div>
      )}
      <LocationLink location={activity.location} obfuscated={obfuscated} hint={activity.hint} />
    </div>
  );
}

function CompetitionBadge({ activity }) {
  if (!isCompetitionActivity(activity)) return null;
  return <span className="actd-team-badge">{activity.isTeamBased ? '🏆 Team competition' : '🏆 Competition'}</span>;
}

function FoodActivity({ activity, obfuscated, canToggle, onToggleDraft }) {
  return (
    <div className={`actd-inline${activity.status === 'draft' ? ' actd-inline--draft' : ''}`}>
      <ActivityHeader activity={activity} obfuscated={obfuscated} defaultIcon="🍽️" />
      <ActivityMeta activity={activity} obfuscated={obfuscated} />
      <div className="actd-inline-status">
        <StatusControls activity={activity} canToggle={canToggle} onToggle={onToggleDraft} />
        <CompetitionBadge activity={activity} />
      </div>
      <CommentsSection activityId={activity.id} masterEventId={activity.masterEventId} />
    </div>
  );
}

function TransportActivity({ activity, obfuscated, canToggle, onToggleDraft }) {
  const mode = activity.transportMode || 'car';
  const icon = TRANSPORT_ICONS[mode] || '🚗';
  const modeLabels = { train: 'Train', flight: 'Flight', car: 'Car', boat: 'Boat' };

  return (
    <div className={`actd-inline${activity.status === 'draft' ? ' actd-inline--draft' : ''}`}>
      <ActivityHeader activity={activity} obfuscated={obfuscated} defaultIcon={icon} />
      {!obfuscated && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 20 }}>{icon}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{modeLabels[mode] || mode}</span>
        </div>
      )}
      <ActivityMeta activity={activity} obfuscated={obfuscated} />
      <div className="actd-inline-status">
        <StatusControls activity={activity} canToggle={canToggle} onToggle={onToggleDraft} />
      </div>
      <CommentsSection activityId={activity.id} masterEventId={activity.masterEventId} />
    </div>
  );
}

function AccommodationActivity({ activity, obfuscated, canToggle, onToggleDraft }) {
  const accType = activity.accommodationType || 'hotel';
  const icon = ACCOMMODATION_ICONS[accType] || '🏨';
  const typeLabels = { hotel: 'Hotel', hostel: 'Hostel', airbnb: 'Airbnb', camping: 'Camping', resort: 'Resort', cabin: 'Cabin' };

  return (
    <div className={`actd-inline${activity.status === 'draft' ? ' actd-inline--draft' : ''}`}>
      <ActivityHeader activity={activity} obfuscated={obfuscated} defaultIcon={icon} />
      {!obfuscated && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 20 }}>{icon}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{typeLabels[accType] || accType}</span>
        </div>
      )}
      <ActivityMeta activity={activity} obfuscated={obfuscated} />
      <div className="actd-inline-status">
        <StatusControls activity={activity} canToggle={canToggle} onToggle={onToggleDraft} />
      </div>
      <CommentsSection activityId={activity.id} masterEventId={activity.masterEventId} />
    </div>
  );
}

function GameActivity({ activity, obfuscated, canToggle, onToggleDraft }) {
  const [scores, setScores] = useState({});
  return (
    <div className={`actd-inline${activity.status === 'draft' ? ' actd-inline--draft' : ''}`}>
      <ActivityHeader activity={activity} obfuscated={obfuscated} defaultIcon="🎮" />
      <ActivityMeta activity={activity} obfuscated={obfuscated} />
      <div className="actd-inline-fields">
        <div className="actd-field">
          <label>Score Input</label>
          <input type="text" placeholder="Enter score..." value={scores.main || ''} onChange={(e) => setScores({ ...scores, main: e.target.value })} />
        </div>
      </div>
      <div className="actd-inline-status">
        <StatusControls activity={activity} canToggle={canToggle} onToggle={onToggleDraft} />
        <CompetitionBadge activity={activity} />
      </div>
      <CommentsSection activityId={activity.id} masterEventId={activity.masterEventId} />
    </div>
  );
}

function CustomActivity({ activity, obfuscated, canToggle, onToggleDraft }) {
  return (
    <div className={`actd-inline${activity.status === 'draft' ? ' actd-inline--draft' : ''}`}>
      <ActivityHeader activity={activity} obfuscated={obfuscated} defaultIcon="📋" />
      <ActivityMeta activity={activity} obfuscated={obfuscated} />
      <div className="actd-inline-status">
        <StatusControls activity={activity} canToggle={canToggle} onToggle={onToggleDraft} />
        <CompetitionBadge activity={activity} />
      </div>
      <CommentsSection activityId={activity.id} masterEventId={activity.masterEventId} />
    </div>
  );
}

function ObfuscatedActivity({ activity, canToggle, onToggleDraft }) {
  return (
    <div className={`actd-inline${activity.status === 'draft' ? ' actd-inline--draft' : ''}`}>
      <ActivityHeader activity={activity} obfuscated defaultIcon="🙈" />
      <ActivityMeta activity={activity} obfuscated />
      <div className="actd-inline-status">
        <StatusControls activity={activity} canToggle={canToggle} onToggle={onToggleDraft} />
      </div>
      <CommentsSection activityId={activity.id} masterEventId={activity.masterEventId} />
    </div>
  );
}

function BettingActivityLayout({ activity, meId, canToggle, onToggleDraft }) {
  const { isActivityAdmin } = useMasterEvent();
  return (
    <div className={`actd-betting${activity.status === 'draft' ? ' actd-betting--draft' : ''}`}>
      <div className="actd-betting-header">
        <span className="actd-inline-icon">🎯</span>
        <h3 className="actd-inline-name">{activity.name}</h3>
        <StatusControls activity={activity} canToggle={canToggle} onToggle={onToggleDraft} />
      </div>
      <nav className="actd-betting-nav">
        <NavLink to={`/me/${meId}/activity/${activity.id}/betting`} end className="actd-betting-link">Dashboard</NavLink>
        <NavLink to={`/me/${meId}/activity/${activity.id}/betting/place`} className="actd-betting-link">Place Bets</NavLink>
        <NavLink to={`/me/${meId}/activity/${activity.id}/betting/my-bets`} className="actd-betting-link">My Bets</NavLink>
        <NavLink to={`/me/${meId}/activity/${activity.id}/betting/events`} className="actd-betting-link">Events</NavLink>
        <NavLink to={`/me/${meId}/activity/${activity.id}/betting/leaderboard`} className="actd-betting-link">Leaderboard</NavLink>
        {isActivityAdmin && <NavLink to={`/me/${meId}/activity/${activity.id}/betting/admin`} className="actd-betting-link">Admin</NavLink>}
      </nav>
      <BettingProvider key={activity.id}>
        <Outlet />
      </BettingProvider>
    </div>
  );
}

function EditActivityPanel({ activity, masterEvent, onSave, onCancel }) {
  return (
    <div style={{ marginBottom: 20, padding: 18, background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 12 }}>
      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>Edit Activity</h4>
      <ActivityForm initial={activity} masterEvent={masterEvent} onSubmit={onSave} onCancel={onCancel} submitLabel="Save Changes" />
    </div>
  );
}

export default function ActivityDetail() {
  const { id: meId, actId } = useParams();
  const { activities, masterEvent, isEventAdmin, isActivityAdmin, updateActivityLocal, loading } = useMasterEvent();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [savingVisibility, setSavingVisibility] = useState(false);

  if (loading) return <div className="actd-loading">Loading activity...</div>;

  const activity = activities.find((a) => a.id === actId);
  if (!activity) return <div className="actd-not-found"><h3>Activity not found</h3><p>This activity doesn't exist or may have been removed.</p></div>;

  const canSeeObfuscatedDetails = isActivityAdmin || isEventAdmin;
  const obfuscated = isActivityObfuscated(activity, masterEvent);
  const isObfuscated = isActivityHidden(activity, masterEvent, canSeeObfuscatedDetails);

  const obfuscationBanner = isObfuscated ? (
    <div style={{
      background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)',
      borderRadius: 10, padding: '10px 14px', marginBottom: 16,
      display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#fbbf24',
    }}>
      <span style={{ fontSize: 18 }}>🙈</span>
      <span>Some details are hidden. The organizer will reveal them when the time is right!</span>
    </div>
  ) : null;

  const handleSaveEdit = async (data) => {
    setError('');
    try {
      await updateActivityLocal(activity.id, data);
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Could not save activity');
    }
  };

  const handleToggleDraft = async () => {
    const next = activity.status === 'draft' ? 'active' : 'draft';
    setError('');
    try {
      await updateActivityLocal(activity.id, { status: next });
    } catch (err) {
      setError(err.message || 'Could not update activity status');
    }
  };

  const handleToggleVisibility = async () => {
    setError('');
    setSavingVisibility(true);
    try {
      await updateActivityLocal(activity.id, { obfuscated: !obfuscated });
    } catch (err) {
      setError(err.message || 'Could not change activity visibility');
    } finally {
      setSavingVisibility(false);
    }
  };

  // Admin controls are separate from the activity's publication status.
  const showEdit = isActivityAdmin && !editing;
  const showToggle = isActivityAdmin && !editing;
  const actionBar = (showEdit || showToggle) ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 12 }}>
      <span style={{ marginRight: 'auto', fontSize: 12, color: obfuscated ? '#fbbf24' : '#4ade80' }}>
        {obfuscated ? 'Obfuscated for members' : 'Visible to members'}
      </span>
      <button type="button" onClick={handleToggleVisibility} disabled={savingVisibility} className="actd-status-toggle">
        {savingVisibility ? 'Saving...' : obfuscated ? 'Reveal activity' : 'Obfuscate activity'}
      </button>
      {showToggle && (
        <button onClick={handleToggleDraft} className="actd-status-toggle"
          title={activity.status === 'draft' ? 'Publish (go live)' : 'Move back to draft'}>
          {activity.status === 'draft' ? '🚀 Publish' : '📝 Move to draft'}
        </button>
      )}
      {showEdit && (
        <button onClick={() => setEditing(true)} style={{
          padding: '5px 12px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)',
          borderRadius: 6, color: '#60a5fa', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>✏️ Edit Activity</button>
      )}
    </div>
  ) : null;
  const editButton = actionBar;
  const errorBanner = error ? (
    <div style={{
      background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 10, padding: '10px 14px', marginBottom: 12, color: '#f87171', fontSize: 13,
    }}>
      {error}
    </div>
  ) : null;

  if (editing) {
    return <>{errorBanner}<EditActivityPanel activity={activity} masterEvent={masterEvent} onSave={handleSaveEdit} onCancel={() => setEditing(false)} /></>;
  }

  const toggleProps = { canToggle: isActivityAdmin, onToggleDraft: handleToggleDraft };
  const isDraft = activity.status === 'draft';
  const wrapDraft = (child) => isDraft ? <div className="actd-draft-frame">{child}</div> : child;
  const hiddenActivity = <ObfuscatedActivity activity={activity} {...toggleProps} />;

  if (isObfuscated) return <>{errorBanner}{obfuscationBanner}{wrapDraft(hiddenActivity)}</>;

  switch (activity.type) {
    case 'teamselection':
      return <>{editButton}{errorBanner}<TeamSelectionActivity activity={activity} /></>;
    case 'betting':
      return <>{editButton}{errorBanner}<BettingActivityLayout activity={activity} meId={meId} {...toggleProps} /></>;
    case 'quiz':
      return <>{editButton}{errorBanner}{obfuscationBanner}{wrapDraft(<QuizActivity activity={activity} />)}</>;
    case 'vote':
      return <>{editButton}{errorBanner}{obfuscationBanner}{wrapDraft(<VoteActivity activity={activity} />)}</>;
    case 'shuffleboard':
      return <>{editButton}{errorBanner}{obfuscationBanner}{wrapDraft(<ShuffleboardActivity activity={activity} />)}</>;
    case 'openbets':
      return <>{editButton}{errorBanner}{obfuscationBanner}{wrapDraft(<OpenBetsActivity activity={activity} />)}</>;
    case 'food':
      return <>{editButton}{errorBanner}{obfuscationBanner}<FoodActivity activity={activity} obfuscated={isObfuscated} {...toggleProps} /></>;
    case 'transport':
      return <>{editButton}{errorBanner}{obfuscationBanner}<TransportActivity activity={activity} obfuscated={isObfuscated} {...toggleProps} /></>;
    case 'accommodation':
      return <>{editButton}{errorBanner}{obfuscationBanner}<AccommodationActivity activity={activity} obfuscated={isObfuscated} {...toggleProps} /></>;
    case 'game':
      return <>{editButton}{errorBanner}{obfuscationBanner}<GameActivity activity={activity} obfuscated={isObfuscated} {...toggleProps} /></>;
    default:
      return <>{editButton}{errorBanner}{obfuscationBanner}<CustomActivity activity={activity} obfuscated={isObfuscated} {...toggleProps} /></>;
  }
}
